/**
 * Square utility functions for coordinate conversions
 */
import type { File, Rank, Square } from "../types/common";
import { FILES, RANKS } from "../types/common";

/** Convert file to index (a=0, h=7) */
export function fileToIndex(file: File): number {
  return FILES.indexOf(file);
}

/** Convert index to file (0=a, 7=h) */
export function indexToFile(index: number): File | null {
  return FILES[index] ?? null;
}

/** Convert rank to index (1=0, 8=7) */
export function rankToIndex(rank: Rank): number {
  return rank - 1;
}

/** Convert index to rank (0=1, 7=8) */
export function indexToRank(index: number): Rank | null {
  return RANKS[index] ?? null;
}

/** Convert square to array indices */
export function squareToIndices(square: Square): {
  fileIndex: number;
  rankIndex: number;
} {
  return {
    fileIndex: fileToIndex(square.file),
    rankIndex: rankToIndex(square.rank),
  };
}

/** Convert array indices to square */
export function indicesToSquare(
  fileIndex: number,
  rankIndex: number,
): Square | null {
  const file = indexToFile(fileIndex);
  const rank = indexToRank(rankIndex);
  if (file === null || rank === null) return null;
  return { file, rank };
}

/** Check if indices are within board bounds */
export function isInBounds(fileIndex: number, rankIndex: number): boolean {
  return fileIndex >= 0 && fileIndex <= 7 && rankIndex >= 0 && rankIndex <= 7;
}

/** Compare two squares for equality */
export function squaresEqual(a: Square, b: Square): boolean {
  return a.file === b.file && a.rank === b.rank;
}

/** Get square notation string (e.g., "e4") */
export function squareToString(square: Square): string {
  return `${square.file}${square.rank}`;
}

/** Parse square notation string (e.g., "e4") */
export function parseSquare(notation: string): Square | null {
  if (notation.length !== 2) return null;

  const file = notation[0]! as File;
  const rank = Number.parseInt(notation[1]!, 10) as Rank;

  if (!FILES.includes(file)) return null;
  if (!RANKS.includes(rank)) return null;

  return { file, rank };
}
